import {
  FEATURE_DEFINITIONS,
  FEATURE_NAMES,
  type FeatureDefinition,
  type FeatureName,
  toEnvVarName,
} from '@/context/FeatureToggleContext/featureDefinitions';
import { shouldShowFeatureToggles } from '@/dev/utils/environment';
import If from '@/storybook/components/If/If';
import getEnvVar from '@/utils/getEnvVar';
import { Logger } from '@/utils/Logger';
import React, { createContext, startTransition, useContext, useEffect, useState } from 'react';

export { FEATURE_DEFINITIONS, FEATURE_NAMES, toEnvVarName };
export type { FeatureDefinition, FeatureName };

export type Features = Partial<Record<FeatureName, boolean>>;

export const getFeatureDefinition = (name: FeatureName): FeatureDefinition | undefined =>
  FEATURE_DEFINITIONS.find(definition => definition.name === name);

export const isFeatureName = (value: string): value is FeatureName => FEATURE_NAMES.some(name => name === value);

export const isFlagExpired = (definition: Pick<FeatureDefinition, 'expiresOn'>, now: Date): boolean => {
  if (!definition.expiresOn) return false;
  const expiry = new Date(definition.expiresOn);
  if (Number.isNaN(expiry.getTime())) return false;
  return expiry.getTime() < now.getTime();
};

type FeatureFlagProps = { feature: FeatureName; children: React.ReactNode };

const moduleName = 'FeatureToggle';

export const FEATURE_OVERRIDES_EVENT = 'ces:feature-overrides-changed';

export const overrideKey = (featureName: FeatureName): string => `CES_FEATURE_OVERRIDE_${featureName}`;

function logEnabledFeatures(features: Features) {
  Object.entries(features).forEach(([feature, isToggled]) => {
    Logger.log(moduleName, `featureFlag "${feature}" is ${isToggled ? 'enabled' : 'disabled'}`);
  });
}

function resolveFeature(featureName: FeatureName, devOverridesEnabled: boolean): boolean {
  const envEnabled = getEnvVar(toEnvVarName(featureName)) === 'true';
  if (!devOverridesEnabled) return envEnabled;
  const override = localStorage.getItem(overrideKey(featureName));
  return override === null ? envEnabled : override === 'true';
}

function getEnvFeatures(): Features {
  const devOverridesEnabled = shouldShowFeatureToggles();
  return Object.fromEntries(
    FEATURE_NAMES.map((featureName): [FeatureName, boolean] => [
      featureName,
      resolveFeature(featureName, devOverridesEnabled),
    ]),
  );
}

const defaultFeatures: Features = Object.fromEntries(
  FEATURE_NAMES.map((featureName): [FeatureName, boolean] => [featureName, false]),
);

const FeatureToggleContext = createContext<Features>(defaultFeatures);

const Provider = FeatureToggleContext.Provider;

export const useFeatureFlags = (): Features => useContext(FeatureToggleContext);

export const useFeatureFlag = (featureFlag: FeatureName): boolean => {
  const features = useContext(FeatureToggleContext);
  // Defensive: a stale flag name can still reach here at runtime via config-driven
  // navigation entries (Entry.featureFlag), which TypeScript cannot check.
  if (typeof features[featureFlag] === 'undefined') {
    Logger.warn(
      moduleName,
      `"${featureFlag}" isn't specified in a list of known feature flags. Please check the content of FEATURE_NAMES.`,
    );
    return false;
  }
  return features[featureFlag];
};

export const FeatureToggleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [features, setFeatures] = useState<Features>(() => {
    const initialFeatures = getEnvFeatures();
    logEnabledFeatures(initialFeatures);
    return initialFeatures;
  });

  useEffect(() => {
    if (!shouldShowFeatureToggles()) return;
    // Toggling a flag remounts gated (often heavy) widgets; keep the dev panel responsive
    // by treating that re-render as a non-urgent transition.
    const handleOverridesChanged = () => startTransition(() => setFeatures(getEnvFeatures()));
    window.addEventListener(FEATURE_OVERRIDES_EVENT, handleOverridesChanged);
    return () => window.removeEventListener(FEATURE_OVERRIDES_EVENT, handleOverridesChanged);
  }, []);

  return <Provider value={features}>{children}</Provider>;
};

export const FeatureFlag: React.FC<FeatureFlagProps> = ({ feature, children }) => {
  const isFeatureOn = useFeatureFlag(feature);
  return <If condition={isFeatureOn}>{children}</If>;
};
